// Submenu — Positioning utilities
//
// Pure geometry functions for placing a panel relative to a trigger.
// No DOM dependency — works with DOMRect objects.
//
// Exports:
//   resolve(name, triggerRect, panelRect) → { top, left }
//     Calculates position by name, flips if overflowing viewport, then clamps.
//
//   auto(triggerRect, panelRect) → { top, left }
//     Tests right/left/top/bottom and picks the one with least overflow.

const GAP = 8

const POSITIONS = {
  right:         (t, p) => ({ top: t.top + t.height / 2 - p.height / 2, left: t.right + GAP }),
  left:          (t, p) => ({ top: t.top + t.height / 2 - p.height / 2, left: t.left - p.width - GAP }),
  top:           (t, p) => ({ top: t.top - p.height - GAP, left: t.left + t.width / 2 - p.width / 2 }),
  bottom:        (t, p) => ({ top: t.bottom + GAP, left: t.left + t.width / 2 - p.width / 2 }),
  'top-right':   (t, p) => ({ top: t.top - p.height - GAP, left: t.right + GAP }),
  'top-left':    (t, p) => ({ top: t.top - p.height - GAP, left: t.left - p.width - GAP }),
  'bottom-right': (t, p) => ({ top: t.bottom + GAP, left: t.right + GAP }),
  'bottom-left':  (t, p) => ({ top: t.bottom + GAP, left: t.left - p.width - GAP }),
}

const FLIP = { right: 'left', left: 'right', top: 'bottom', bottom: 'top' }

function overflow(pos, p) {
  return Math.max(0, GAP - pos.left) +
    Math.max(0, GAP - pos.top) +
    Math.max(0, pos.left + p.width - (innerWidth - GAP)) +
    Math.max(0, pos.top + p.height - (innerHeight - GAP))
}

function clamp(pos, p) {
  return {
    top: Math.max(GAP, Math.min(pos.top, innerHeight - p.height - GAP)),
    left: Math.max(GAP, Math.min(pos.left, innerWidth - p.width - GAP)),
  }
}

export function resolve(name, t, p) {
  const parts = name.split('-')
  const pos = POSITIONS[name](t, p)

  const vPart = parts.find(p => p === 'top' || p === 'bottom')
  if (vPart && (pos.top < GAP || pos.top + p.height > innerHeight - GAP)) {
    parts[parts.indexOf(vPart)] = FLIP[vPart]
  }

  const hPart = parts.find(p => p === 'left' || p === 'right')
  if (hPart && (pos.left < GAP || pos.left + p.width > innerWidth - GAP)) {
    parts[parts.indexOf(hPart)] = FLIP[hPart]
  }

  const flipped = parts.join('-')
  return clamp(flipped !== name ? POSITIONS[flipped](t, p) : pos, p)
}

export function auto(t, p) {
  const best = ['right', 'left', 'top', 'bottom']
    .map(name => POSITIONS[name](t, p))
    .reduce((a, b) => overflow(a, p) <= overflow(b, p) ? a : b)

  return clamp(best, p)
}
